import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ORDERS_API_SAVED_SEARCHES } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import {
  defaultFilters,
  FiltersParamsType,
} from 'domains/Orders/Orders/ordersService';
import {
  cleanSavedSearchStatus,
  configureSavedSearches,
  createSavedSearch,
  fetchSavedSearches,
  fetchSavedSearchesCountOrders,
  getSavedSearches,
  getSavedSearchesCount,
  getSavedSearchesCountStatus,
  updateSavedSearch,
} from 'domains/Orders/Orders/ordersSlice';
import {
  defaultSavedSearches,
  SavedSearchInterface,
} from 'domains/Orders/Orders/ordersSlice/savedSearches';

export function useSavedSearches() {
  const savedSearchesCount = useSelector(getSavedSearchesCount);
  const savedSearchesCountStatus = useSelector(getSavedSearchesCountStatus);
  const {
    data: savedSearches,
    fetchStatus: savedSearchesStatus,
    createStatus: createSavedSearchStatus,
    configStatus: configSavedSearchesStatus,
  } = useSelector(getSavedSearches);
  const dispatch = useAppDispatch();
  const [isApiSavedSearchesEnabled] = useHasTags(ORDERS_API_SAVED_SEARCHES);
  const cleanApiSavedSearchStatus = useCallback(
    () => dispatch(cleanSavedSearchStatus()),
    [dispatch],
  );

  const digestFilters = (filters: FiltersParamsType) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onlyFilters = (({ perPage, page, ...filters }) => filters)(filters);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onlyDefaultFilters = (({ perPage, page, ...newFilters }) =>
      newFilters)(defaultFilters);

    // We keep only keys that differ from default filters
    const filterObject = Object.entries(onlyFilters).reduce(
      (acc, [key, value]) => {
        if (JSON.stringify(value) !== JSON.stringify(onlyDefaultFilters[key])) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    return JSON.stringify(filterObject);
  };

  const createApiSavedSearch = useCallback(
    (name: string, filters: FiltersParamsType) => {
      dispatch(
        createSavedSearch({
          name,
          filter: digestFilters(filters),
        }),
      );
    },
    [dispatch],
  );

  const updateApiSavedSearch = useCallback(
    (id: string, name: string, filters: FiltersParamsType) => {
      dispatch(
        updateSavedSearch({
          id,
          payload: {
            name,
            filter: digestFilters(filters),
          },
        }),
      );
    },
    [dispatch],
  );

  const configureApiSavedSearches = useCallback(
    (configSavedSearches: SavedSearchInterface[]) => {
      dispatch(
        configureSavedSearches(
          configSavedSearches.map(
            ({ id, hidden, deleted, isDefault, key }) => ({
              id: isDefault ? key : id,
              hidden,
              delete: deleted ?? false,
            }),
          ),
        ),
      );
    },
    [dispatch],
  );

  const querySavedSearches = useCallback(() => {
    if (isApiSavedSearchesEnabled) {
      dispatch(fetchSavedSearches());
    }
  }, [dispatch, isApiSavedSearchesEnabled]);

  const fetchSavedSearchesCount = useCallback(async () => {
    await dispatch(
      fetchSavedSearchesCountOrders(defaultSavedSearches),
    ).unwrap();
  }, [dispatch]);

  const buildQueryParamsFromSavedSearch = (id: string): string => {
    const savedSearchesToUse = savedSearches.length
      ? savedSearches
      : defaultSavedSearches;

    const savedSearch = savedSearchesToUse.filter((savedSearch) =>
      savedSearch.isDefault ? savedSearch.key === id : savedSearch.id === id,
    )[0];
    if (!savedSearch) {
      return '';
    }
    const searchParams = new URLSearchParams();

    Object.entries(savedSearch.filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, v));
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });

    return searchParams.toString();
  };

  return {
    savedSearches: isApiSavedSearchesEnabled
      ? savedSearches
      : defaultSavedSearches,
    savedSearchesStatus: isApiSavedSearchesEnabled
      ? savedSearchesStatus
      : 'success',
    createSavedSearchStatus,
    configSavedSearchesStatus,
    cleanSavedSearchStatus: cleanApiSavedSearchStatus,
    configureSavedSearches: configureApiSavedSearches,
    isApiSavedSearchesEnabled,
    fetchSavedSearches: querySavedSearches,
    createSavedSearch: createApiSavedSearch,
    updateSavedSearch: updateApiSavedSearch,
    fetchSavedSearchesCount,
    savedSearchesCount,
    savedSearchesCountStatus,
    buildQueryParamsFromSavedSearch,
  };
}
